// Sequential Division → Branch → Section picker (onboarding + dashboard).

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import {
  SECTION_BG_LIGHTEN_RATIO,
  getStructureCardBgClass,
  getStructureBreadcrumbBgClass,
} from '../onboarding/onboardingStructureCardTheme';
import {
  STRUCTURE_LEVELS,
  STRUCTURE_TAGS_MIN,
  STRUCTURE_TAGS_MAX,
  DEFAULT_ORG_STRUCTURE_LOAD_URL,
  buildStructureChildrenIndex,
  getStructureChildrenOf,
  pickStructureBackgroundHlsUrl,
  filterMembershipCatalogForSection,
  sectionHasValidMembershipTitles,
  SECTION_WITHOUT_VALID_TITLES_MESSAGE,
} from './orgStructureSelectorUtils';
import MembershipAssignmentPicker from './MembershipAssignmentPicker';

/**
 * @param {object} props
 * @param {string} [props.loadOrgStructureUrl]
 * @param {{ divisionId: number|null, branchId: number|null, sectionId: number|null }} [props.initialSelection]
 * @param {number[]} [props.initialTagIds]
 * @param {{ id: number, label: string }[]} [props.masterTags]
 * @param {(choice: { divisionId: number|null, branchId: number|null, sectionId: number|null, membershipAssignmentId: number|null, tags: number[] }) => Promise<void>} props.onConfirm
 * @param {string} [props.confirmLabel]
 * @param {string} [props.validateHelpText]
 * @param {boolean} [props.isValidationActive]
 * @param {string} [props.tagsHelpIdPrefix]
 * @param {string} [props.wrapClassName]
 * @param {string} [props.cardItemClassName] — override list item class (dashboard editor sizing)
 * @param {(url: string|null) => void} [props.onStructureBackgroundVideoChange]
 * @param {boolean} [props.showInternalConfirm]
 * @param {boolean} [props.showFooterDock] — tip, tags, and inline validate panel (off on dashboard)
 * @param {(state: { canConfirm: boolean, tagsAreValid: boolean, currentLevel: string }) => void} [props.onDraftChange]
 * @param {boolean} [props.lockMembershipEdit] — skip membership step (e.g. pending structure request)
 * @param {boolean} [props.lockSectionsWithoutValidTitles] — disable section cards with no membership titles
 * @param {string} [props.sectionWithoutValidTitlesMessage]
 */
const OrgStructureSelector = forwardRef(function OrgStructureSelector(
  {
  loadOrgStructureUrl = DEFAULT_ORG_STRUCTURE_LOAD_URL,
  initialSelection = {
    divisionId: null,
    branchId: null,
    sectionId: null,
    membershipAssignmentId: null,
  },
  initialTagIds = [],
  masterTags = [],
  onConfirm,
  confirmLabel = 'Confirm',
  validateHelpText = 'Review your choices, then confirm.',
  isValidationActive = false,
  tagsHelpIdPrefix = 'org-structure',
  wrapClassName = '',
  cardItemClassName = 'members-onboarding-structure__card-item',
  onStructureBackgroundVideoChange,
  showInternalConfirm = true,
  showFooterDock = true,
  onDraftChange,
  lockMembershipEdit = false,
  lockSectionsWithoutValidTitles = false,
  sectionWithoutValidTitlesMessage = SECTION_WITHOUT_VALID_TITLES_MESSAGE,
},
ref,
) {
  const [divisions, setDivisions] = useState(null);
  const [membershipCatalog, setMembershipCatalog] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [selected, setSelected] = useState(() => ({
    divisionId: initialSelection.divisionId ?? null,
    branchId: initialSelection.branchId ?? null,
    sectionId: initialSelection.sectionId ?? null,
    membershipAssignmentId: initialSelection.membershipAssignmentId ?? null,
  }));

  const [selectedTagIds, setSelectedTagIds] = useState(() => {
    const s = new Set();
    for (const id of initialTagIds) {
      const n = parseInt(id, 10);
      if (Number.isInteger(n) && n > 0) s.add(n);
    }
    return s;
  });

  const safeTags = Array.isArray(masterTags) ? masterTags : [];
  const hasTags = safeTags.length > 0;
  const selectedTagsCount = selectedTagIds.size;
  const tagsAreValid =
    !showFooterDock ||
    !hasTags ||
    (selectedTagsCount >= STRUCTURE_TAGS_MIN && selectedTagsCount <= STRUCTURE_TAGS_MAX);

  const toggleTagId = useCallback((tagId) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else if (next.size < STRUCTURE_TAGS_MAX) {
        next.add(tagId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!hasTags) {
      if (selectedTagIds.size > 0) setSelectedTagIds(new Set());
      return;
    }
    const activeIds = new Set(safeTags.map((t) => t.id));
    let drift = false;
    selectedTagIds.forEach((id) => {
      if (!activeIds.has(id)) drift = true;
    });
    if (!drift) return;
    setSelectedTagIds((prev) => {
      const next = new Set();
      prev.forEach((id) => {
        if (activeIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [safeTags, hasTags, selectedTagIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(loadOrgStructureUrl, {
          withCredentials: true,
        });
        if (cancelled) return;
        const rows = Array.isArray(data?.divisions) ? data.divisions : [];
        const catalog = Array.isArray(data?.membershipAssignments)
          ? data.membershipAssignments
          : [];
        setDivisions(rows);
        setMembershipCatalog(catalog);
        setLoadError(null);
      } catch (e) {
        if (!cancelled) {
          setDivisions([]);
          setMembershipCatalog([]);
          setLoadError(
            e?.response?.data?.error ||
              e?.response?.data?.message ||
              e?.message ||
              'Unable to load the organization structure.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOrgStructureUrl]);

  const childrenIndex = useMemo(
    () => (divisions ? buildStructureChildrenIndex(divisions) : new Map()),
    [divisions],
  );
  const byId = useMemo(() => {
    const m = new Map();
    if (divisions) for (const d of divisions) m.set(d.id, d);
    return m;
  }, [divisions]);

  const structureBgHlsUrl = useMemo(
    () => (divisions ? pickStructureBackgroundHlsUrl(byId, selected) : null),
    [divisions, byId, selected],
  );

  useEffect(() => {
    if (!onStructureBackgroundVideoChange) return undefined;
    onStructureBackgroundVideoChange(structureBgHlsUrl);
    return undefined;
  }, [structureBgHlsUrl, onStructureBackgroundVideoChange]);

  useEffect(() => {
    if (!onStructureBackgroundVideoChange) return undefined;
    return () => {
      onStructureBackgroundVideoChange(null);
    };
  }, [onStructureBackgroundVideoChange]);

  const divisionsList = useMemo(() => {
    if (!divisions || divisions.length === 0) return [];
    const absoluteRootIds = new Set(
      divisions.filter((d) => d.parentId == null).map((d) => d.id),
    );
    if (absoluteRootIds.size === 0) return [];
    return divisions.filter((d) => absoluteRootIds.has(d.parentId));
  }, [divisions]);

  const branchsList = useMemo(
    () =>
      selected.divisionId != null
        ? getStructureChildrenOf(childrenIndex, selected.divisionId)
        : [],
    [childrenIndex, selected.divisionId],
  );

  const sectionsList = useMemo(
    () =>
      selected.branchId != null
        ? getStructureChildrenOf(childrenIndex, selected.branchId)
        : [],
    [childrenIndex, selected.branchId],
  );

  const membershipOptions = useMemo(
    () => filterMembershipCatalogForSection(membershipCatalog, selected.sectionId, byId),
    [membershipCatalog, selected.sectionId, byId],
  );

  const membershipRequired = selected.sectionId != null && !lockMembershipEdit;

  const currentLevel = useMemo(() => {
    if (selected.divisionId == null) return 'division';
    if (branchsList.length > 0 && selected.branchId == null) return 'branch';
    if (
      selected.branchId != null &&
      sectionsList.length > 0 &&
      selected.sectionId == null
    ) {
      return 'section';
    }
    if (membershipRequired && selected.membershipAssignmentId == null) {
      return 'membership';
    }
    return 'validate';
  }, [
    selected,
    branchsList.length,
    sectionsList.length,
    membershipRequired,
  ]);

  const breadcrumbItems = useMemo(() => {
    const items = [];
    if (selected.divisionId != null) {
      const d = byId.get(selected.divisionId);
      if (d) items.push({ levelKey: 'division', division: d });
    }
    if (selected.branchId != null) {
      const b = byId.get(selected.branchId);
      if (b) items.push({ levelKey: 'branch', division: b });
    }
    if (selected.sectionId != null) {
      const s = byId.get(selected.sectionId);
      if (s) items.push({ levelKey: 'section', division: s });
    }
    return items;
  }, [selected, byId]);

  const resetFromLevel = useCallback((levelKey) => {
    setSelected((prev) => {
      if (levelKey === 'division') {
        return {
          divisionId: null,
          branchId: null,
          sectionId: null,
          membershipAssignmentId: null,
        };
      }
      if (levelKey === 'branch') {
        return { ...prev, branchId: null, sectionId: null, membershipAssignmentId: null };
      }
      if (levelKey === 'section') {
        return { ...prev, sectionId: null, membershipAssignmentId: null };
      }
      if (levelKey === 'membership') {
        return { ...prev, membershipAssignmentId: null };
      }
      return prev;
    });
  }, []);

  const pickCard = useCallback((levelKey, id) => {
    if (levelKey === 'division') {
      setSelected({
        divisionId: id,
        branchId: null,
        sectionId: null,
        membershipAssignmentId: null,
      });
    } else if (levelKey === 'branch') {
      setSelected((p) => ({
        ...p,
        branchId: id,
        sectionId: null,
        membershipAssignmentId: null,
      }));
    } else if (levelKey === 'section') {
      setSelected((p) => ({ ...p, sectionId: id, membershipAssignmentId: null }));
    }
  }, []);

  const pickMembership = useCallback((assignmentId) => {
    setSelected((p) => ({ ...p, membershipAssignmentId: assignmentId }));
  }, []);

  const handleValidate = useCallback(async () => {
    if (submitting || !onConfirm) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm({
        divisionId: selected.divisionId,
        branchId: selected.branchId,
        sectionId: selected.sectionId,
        membershipAssignmentId: selected.membershipAssignmentId,
        tags: Array.from(selectedTagIds),
      });
    } catch (e) {
      setSubmitError(e?.message || 'Unable to save your choices.');
    } finally {
      setSubmitting(false);
    }
  }, [onConfirm, submitting, selected, selectedTagIds]);

  const membershipIsValid =
    !membershipRequired ||
    (membershipOptions.length > 0 && selected.membershipAssignmentId != null);

  const canConfirm = currentLevel === 'validate' && tagsAreValid && membershipIsValid;

  useEffect(() => {
    if (!onDraftChange) return;
    onDraftChange({ canConfirm, tagsAreValid, currentLevel });
  }, [onDraftChange, canConfirm, tagsAreValid, currentLevel]);

  useImperativeHandle(
    ref,
    () => ({
      confirm: handleValidate,
      canConfirm,
    }),
    [handleValidate, canConfirm],
  );

  const tagsHelpId = `org-structure-tags-help-${tagsHelpIdPrefix}`;

  if (divisions === null) {
    return (
      <div className={`members-onboarding-steps__structure-wrap ${wrapClassName}`.trim()}>
        <p className="members-onboarding-steps__structure-note">Loading organization…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`members-onboarding-steps__structure-wrap ${wrapClassName}`.trim()}>
        <p className="members-onboarding-steps__error" role="alert">
          {loadError}
        </p>
      </div>
    );
  }

  if (divisionsList.length === 0) {
    return (
      <div className={`members-onboarding-steps__structure-wrap ${wrapClassName}`.trim()}>
        <p className="members-onboarding-steps__structure-note">
          No division is currently available. Please contact an administrator.
        </p>
      </div>
    );
  }

  let levelDef = null;
  let visibleCards = [];
  if (currentLevel === 'division') {
    levelDef = STRUCTURE_LEVELS[0];
    visibleCards = divisionsList;
  } else if (currentLevel === 'branch') {
    levelDef = STRUCTURE_LEVELS[1];
    visibleCards = branchsList;
  } else if (currentLevel === 'section') {
    levelDef = STRUCTURE_LEVELS[2];
    visibleCards = sectionsList;
  }

  const selectedSectionName =
    selected.sectionId != null
      ? byId.get(selected.sectionId)?.divisionFullName || ''
      : '';

  const wrapClasses = [
    'members-onboarding-steps__structure-wrap',
    'members-onboarding-structure',
    wrapClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClasses}>
      <div className="members-onboarding-structure__topStack">
        {breadcrumbItems.length > 0 ? (
          <ol className="members-onboarding-structure__breadcrumb" aria-label="Selected hierarchy">
            {breadcrumbItems.map((item, idx) => {
              const eyebrowLabel =
                item.levelKey === 'division'
                  ? 'Division'
                  : item.levelKey === 'branch'
                    ? 'Branch'
                    : 'Section';
              const bcLightenRatio =
                item.levelKey === 'section' ? SECTION_BG_LIGHTEN_RATIO : 0;
              const bgClass = getStructureBreadcrumbBgClass(
                item.division.elementColor1,
                item.division.elementColor2,
                bcLightenRatio,
              );
              const btnClassName = bgClass
                ? `members-onboarding-structure__breadcrumb-btn ${bgClass}`
                : 'members-onboarding-structure__breadcrumb-btn';
              return (
                <li key={item.levelKey} className="members-onboarding-structure__breadcrumb-item">
                  <button
                    type="button"
                    className={btnClassName}
                    onClick={() => resetFromLevel(item.levelKey)}
                    title={item.division.divisionFullName}
                  >
                    {item.division.divisionImageUrl ? (
                      <img
                        className="members-onboarding-structure__breadcrumb-logo"
                        src={item.division.divisionImageUrl}
                        alt=""
                        aria-hidden
                      />
                    ) : (
                      <span
                        className="members-onboarding-structure__breadcrumb-logo members-onboarding-structure__breadcrumb-logo--placeholder"
                        aria-hidden
                      />
                    )}
                    <span className="members-onboarding-structure__breadcrumb-text">
                      <span className="members-onboarding-structure__breadcrumb-eyebrow">
                        {eyebrowLabel}
                      </span>
                      <span className="members-onboarding-structure__breadcrumb-label">
                        {item.division.divisionFullName}
                      </span>
                    </span>
                  </button>
                  {idx < breadcrumbItems.length - 1 ? (
                    <span className="members-onboarding-structure__breadcrumb-sep" aria-hidden>
                      ›
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : null}

        {currentLevel === 'membership' ? (
          <MembershipAssignmentPicker
            assignments={membershipOptions}
            selectedId={selected.membershipAssignmentId}
            onSelect={pickMembership}
            sectionName={selectedSectionName}
          />
        ) : null}

        {currentLevel === 'validate' &&
        membershipRequired &&
        selected.membershipAssignmentId != null &&
        membershipOptions.length > 0 ? (
          <MembershipAssignmentPicker
            assignments={membershipOptions}
            selectedId={selected.membershipAssignmentId}
            onSelect={pickMembership}
            sectionName={selectedSectionName}
          />
        ) : null}

        {currentLevel !== 'validate' && currentLevel !== 'membership' && levelDef ? (
          <>
            <h3 className="members-onboarding-structure__level-heading">{levelDef.heading}</h3>
            <ul className="members-onboarding-structure__cards">
              {visibleCards.map((card) => {
                const lightenRatio = levelDef.key === 'section' ? SECTION_BG_LIGHTEN_RATIO : 0;
                const bgClass = getStructureCardBgClass(
                  card.elementColor1,
                  card.elementColor2,
                  lightenRatio,
                );
                const isSectionLocked =
                  lockSectionsWithoutValidTitles &&
                  levelDef.key === 'section' &&
                  !sectionHasValidMembershipTitles(membershipCatalog, card.id, byId);
                const cardClassName =
                  (bgClass
                    ? `members-onboarding-structure__card ${bgClass}`
                    : 'members-onboarding-structure__card') +
                  (isSectionLocked ? ' members-onboarding-structure__card--locked' : '');
                return (
                  <li key={card.id} className={cardItemClassName}>
                    <button
                      type="button"
                      className={cardClassName}
                      onClick={isSectionLocked ? undefined : () => pickCard(levelDef.key, card.id)}
                      disabled={isSectionLocked}
                      aria-disabled={isSectionLocked ? true : undefined}
                      aria-label={
                        isSectionLocked
                          ? `${card.divisionFullName} — unavailable`
                          : `${levelDef.ctaHover} — ${card.divisionFullName}`
                      }
                    >
                      <div className="members-onboarding-structure__card-header">
                        {card.divisionImageUrl ? (
                          <img
                            className="members-onboarding-structure__card-logo"
                            src={card.divisionImageUrl}
                            alt=""
                            aria-hidden
                          />
                        ) : (
                          <span
                            className="members-onboarding-structure__card-logo members-onboarding-structure__card-logo--placeholder"
                            aria-hidden
                          />
                        )}
                        <div className="members-onboarding-structure__card-headings">
                          <span className="members-onboarding-structure__card-title">
                            {card.divisionFullName}
                          </span>
                          {card.elementDetails ? (
                            <span className="members-onboarding-structure__card-subtitle">
                              {card.elementDetails}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {isSectionLocked ? (
                        <p
                          className="members-onboarding-structure__card-lock-msg"
                          role="status"
                        >
                          {sectionWithoutValidTitlesMessage}
                        </p>
                      ) : (
                        <span className="members-onboarding-structure__card-cta" aria-hidden>
                          <span className="members-onboarding-structure__card-cta-default">
                            {levelDef.ctaDefault}
                          </span>
                          <span className="members-onboarding-structure__card-cta-hover">
                            {levelDef.ctaHover}
                          </span>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}
      </div>

      {showFooterDock &&
      ((currentLevel !== 'validate' && currentLevel !== 'membership' && levelDef) ||
        currentLevel === 'membership' ||
        hasTags ||
        currentLevel === 'validate') ? (
        <div className="members-onboarding-structure__footerDock">
          {currentLevel !== 'validate' && currentLevel !== 'membership' && levelDef ? (
            <aside className="members-onboarding-structure__tip" role="note">
              <span className="members-onboarding-structure__tip-icon" aria-hidden>
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  focusable="false"
                >
                  <path d="M9 18h6" />
                  <path d="M10 21h4" />
                  <path d="M12 3a6 6 0 0 0-4 10.5c.9.9 1.5 2 1.5 3V15h5v-1.5c0-1 .6-2.1 1.5-3A6 6 0 0 0 12 3Z" />
                </svg>
              </span>
              <div className="members-onboarding-structure__tip-body">
                <p className="members-onboarding-structure__tip-title">
                  Not sure which {levelDef.key} to choose?
                </p>
                <p className="members-onboarding-structure__tip-text">
                  You can{' '}
                  <span className="members-onboarding-structure__tip-accent">change</span>{' '}
                  this later in your organization settings.
                </p>
              </div>
            </aside>
          ) : null}

          {hasTags || currentLevel === 'validate' ? (
            <div className="members-onboarding-structure__bottomDock">
              {hasTags ? (
                <section
                  className="members-onboarding-structure__tags-wrap"
                  aria-labelledby={tagsHelpId}
                >
                  <p id={tagsHelpId} className="members-onboarding-structure__tags-help">
                    Select between {STRUCTURE_TAGS_MIN} and {STRUCTURE_TAGS_MAX} tags that best
                    describe what you enjoy the most in-game.
                  </p>
                  <ul
                    className="members-onboarding-structure__tags"
                    role="listbox"
                    aria-multiselectable="true"
                  >
                    {safeTags.map((tag) => {
                      const isSelected = selectedTagIds.has(tag.id);
                      const atMax = selectedTagsCount >= STRUCTURE_TAGS_MAX;
                      const isLocked = !isSelected && atMax;
                      const cls =
                        'members-onboarding-structure__tag' +
                        (isSelected ? ' members-onboarding-structure__tag--selected' : '') +
                        (isLocked ? ' members-onboarding-structure__tag--locked' : '');
                      return (
                        <li key={tag.id} className="members-onboarding-structure__tag-item">
                          <button
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={cls}
                            onClick={() => toggleTagId(tag.id)}
                            disabled={isLocked}
                          >
                            {tag.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="members-onboarding-structure__tags-count" aria-live="polite">
                    {selectedTagsCount} / {STRUCTURE_TAGS_MAX} selected
                    {selectedTagsCount < STRUCTURE_TAGS_MIN
                      ? ` — pick at least ${STRUCTURE_TAGS_MIN - selectedTagsCount} more.`
                      : ''}
                  </p>
                </section>
              ) : null}

              {currentLevel === 'validate' ? (
                <div className="members-onboarding-structure__validate-wrap">
                  <p className="members-onboarding-structure__validate-help">{validateHelpText}</p>
                  {membershipRequired && !membershipIsValid ? (
                    <p className="members-onboarding-structure__validate-help" role="status">
                      Select your membership title before confirming.
                    </p>
                  ) : null}
                  {hasTags && !tagsAreValid ? (
                    <p className="members-onboarding-structure__validate-help" role="status">
                      You also need to select between {STRUCTURE_TAGS_MIN} and {STRUCTURE_TAGS_MAX}{' '}
                      tags above before validating.
                    </p>
                  ) : null}
                  {submitError ? (
                    <p className="members-onboarding-steps__error" role="alert">
                      {submitError}
                    </p>
                  ) : null}
                  {showInternalConfirm ? (
                    <button
                      type="button"
                      className="members-onboarding-structure__cta"
                      onClick={handleValidate}
                      disabled={submitting || !tagsAreValid || !membershipIsValid}
                    >
                      {submitting ? 'Saving…' : confirmLabel}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

export default OrgStructureSelector;
